/**
 * 確認コードの入力欄とボタン（SCR-002 の otp 段・ReloginDialog で共通）。
 * layout="login" はログイン画面用で、旧PCRM（pages/Login.css）の見た目：見出しは欄の上、コードは中央寄せ、
 * 欄のすぐ下に「有効期限: m:ss」（切れたら「認証コードの有効期限が切れました」）、
 * ボタンは横幅いっぱいで桁がそろうまで押せない、「コードを再送信 | 別のメールアドレスでログイン」を中央に小さく並べる。
 */
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { AUTH_CODE_LENGTH, FE_MESSAGES, diffSecondsUtc, nowUtc, zAuthCode } from '@pcrm/shared';
import { validationText } from '../../../lib/messages';

const CODE_INPUT_ID = 'pcrm-login-code';

export interface CodeLoginFieldsProps {
  submit_label: string;
  code_error: string | null;
  is_verifying: boolean;
  is_sending: boolean;
  resend_wait_seconds: number;
  onVerify: (code: string) => void;
  onResend: () => void;
  layout?: 'dialog' | 'login';
  /** layout="login" のとき「コードを再送信」の右に区切り線つきで並べる操作 */
  secondary_action?: ReactNode;
  /** layout="login" のとき、欄の下に残り時間を出す確認コードの有効期限（UTC の ISO 文字列） */
  expires_at?: string | null;
}

/** 旧PCRM の .login-card .form-label（欄の上の見出し） */
export const LOGIN_LABEL_SX = {
  display: 'block',
  fontSize: 14,
  fontWeight: 600,
  lineHeight: 1.5,
  mb: 0.75,
  letterSpacing: '0.02em',
} as const;

/** 旧PCRM のログイン画面の入力欄（.form-input：高さ 42px・薄い灰色の地・#ddd の枠・角 6px） */
export const LOGIN_INPUT_SX = {
  '& .MuiOutlinedInput-root': { borderRadius: '6px', backgroundColor: '#F1F5F9' },
  '& .MuiOutlinedInput-root:not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
    borderColor: '#DDDDDD',
  },
  '& .MuiOutlinedInput-input': { py: '11px', px: '12px', fontSize: 14 },
} as const;

/** 旧PCRM の .btn.btn-primary.btn-block（高さ 32px・横幅いっぱい） */
export const LOGIN_SUBMIT_SX = {
  lineHeight: 1.5,
  py: '6px',
  '&.Mui-disabled': { backgroundColor: 'primary.main', color: 'primary.contrastText', opacity: 0.6 },
} as const;

function formatRemaining(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

function remainingOf(expires_at: string): number {
  return Math.max(0, Math.floor(diffSecondsUtc(nowUtc(), expires_at)));
}

/** 確認コードの残り時間（秒）。期限が無ければ null */
function useRemainingSeconds(expires_at: string | null | undefined): number | null {
  const [remaining, setRemaining] = useState<number | null>(expires_at ? remainingOf(expires_at) : null);
  useEffect(() => {
    if (!expires_at) {
      setRemaining(null);
      return undefined;
    }
    setRemaining(remainingOf(expires_at));
    const timer = setInterval(() => {
      const next = remainingOf(expires_at);
      setRemaining(next);
      if (next === 0) {
        clearInterval(timer);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [expires_at]);
  return remaining;
}

/** 旧PCRM の .otp-actions .link-button（小さいオレンジの文字ボタン） */
export const LOGIN_LINK_BUTTON_SX = {
  minHeight: 0,
  minWidth: 0,
  px: 0.75,
  py: 0.5,
  fontSize: 12,
  fontWeight: 400,
  whiteSpace: 'nowrap',
  '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
  '&.Mui-disabled': { color: 'text.secondary' },
} as const;

export function CodeLoginFields(props: CodeLoginFieldsProps) {
  const { onVerify, onResend } = props;
  const [code, setCode] = useState('');
  const [local_error, setLocalError] = useState<string | null>(null);
  const is_login_layout = props.layout === 'login';
  const remaining_seconds = useRemainingSeconds(is_login_layout ? props.expires_at : null);
  const code_input_ref = useRef<HTMLInputElement>(null);
  // ログイン画面では確認コードの段に移ったら欄にカーソルを置く（旧PCRM と同じ）
  useEffect(() => {
    if (is_login_layout) {
      code_input_ref.current?.focus();
    }
  }, [is_login_layout]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (props.is_verifying) {
      return;
    }
    const parsed = zAuthCode.safeParse(code);
    if (!parsed.success) {
      setLocalError(validationText(parsed.error.issues[0]?.message) ?? null);
      return;
    }
    setLocalError(null);
    onVerify(parsed.data);
  };

  const error_text = local_error ?? props.code_error;
  const resend_label =
    props.resend_wait_seconds > 0 ? `コードを再送信（${props.resend_wait_seconds} 秒後）` : 'コードを再送信';
  const submit_content = props.is_verifying ? <CircularProgress size={16} color="inherit" /> : props.submit_label;
  const submit_aria_label = props.is_verifying ? FE_MESSAGES['FE-MSG-I-014'].text : undefined;

  if (is_login_layout) {
    return (
      <Box component="form" noValidate autoComplete="off" onSubmit={submit}>
        <Typography component="label" htmlFor={CODE_INPUT_ID} sx={LOGIN_LABEL_SX}>
          {`認証コード（${AUTH_CODE_LENGTH}桁）`}
        </Typography>
        <TextField
          id={CODE_INPUT_ID}
          value={code}
          placeholder={'0'.repeat(AUTH_CODE_LENGTH)}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, AUTH_CODE_LENGTH))}
          error={Boolean(error_text)}
          autoComplete="one-time-code"
          inputRef={code_input_ref}
          sx={LOGIN_INPUT_SX}
          inputProps={{
            inputMode: 'numeric',
            maxLength: AUTH_CODE_LENGTH,
            'aria-required': true,
            'aria-describedby': `${CODE_INPUT_ID}-help`,
            style: { textAlign: 'center', fontSize: 16 },
          }}
        />
        <Box id={`${CODE_INPUT_ID}-help`}>
          {error_text && (
            <Typography component="p" sx={{ mt: 0.5, fontSize: 12, color: 'error.main' }}>
              {error_text}
            </Typography>
          )}
          {remaining_seconds !== null && (
            <Typography
              component="p"
              sx={{ mt: 1, fontSize: 13, color: remaining_seconds > 0 ? 'text.secondary' : 'error.main' }}
            >
              {remaining_seconds > 0
                ? `有効期限: ${formatRemaining(remaining_seconds)}`
                : '認証コードの有効期限が切れました'}
            </Typography>
          )}
        </Box>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={props.is_verifying || code.length !== AUTH_CODE_LENGTH}
          aria-label={submit_aria_label}
          sx={{ ...LOGIN_SUBMIT_SX, mt: 2.5 }}
        >
          {submit_content}
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'nowrap', mt: 2 }}>
          <Button
            variant="text"
            disabled={props.resend_wait_seconds > 0 || props.is_sending}
            onClick={onResend}
            sx={LOGIN_LINK_BUTTON_SX}
          >
            {resend_label}
          </Button>
          {props.secondary_action && (
            <>
              <Box component="span" aria-hidden="true" sx={{ color: 'divider', mx: 0.5, flexShrink: 0 }}>
                |
              </Box>
              {props.secondary_action}
            </>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box component="form" noValidate onSubmit={submit}>
      <TextField
        label={`認証コード（${AUTH_CODE_LENGTH}桁）`}
        required
        value={code}
        onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, AUTH_CODE_LENGTH))}
        error={Boolean(error_text)}
        helperText={error_text ?? ' '}
        autoComplete="one-time-code"
        inputProps={{ inputMode: 'numeric', maxLength: AUTH_CODE_LENGTH }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Button type="submit" variant="contained" disabled={props.is_verifying} aria-label={submit_aria_label}>
          {submit_content}
        </Button>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
        <Button variant="text" disabled={props.resend_wait_seconds > 0 || props.is_sending} onClick={onResend}>
          {resend_label}
        </Button>
      </Box>
    </Box>
  );
}
